import { useEffect, useRef, useState } from "react";
import { Eye, FileCheck2, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Textarea } from "@/components/ui/textarea";
import { OrderFieldsEditor } from "@/components/admin/OrderFieldsEditor";
import { ProformaSettings } from "@/components/admin/ProformaSettings";

const HINTS = [
  ["# Tytuł", "wyśrodkowany tytuł"],
  ["## Nagłówek", "nagłówek sekcji"],
  ["1. Tekst", "punkt numerowany"],
  ["a. Tekst", "podpunkt literowy"],
  ["**tekst**", "wytłuszczenie"],
  ["[[PAGEBREAK]]", "nowa strona"],
  ["[[SIGNATURES]]", "miejsce na podpisy"],
];

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";

export function ContractEditor() {
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [syncingOwu, setSyncingOwu] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/admin/contract-template").then((r) => {
      setData(r.data);
      setTemplate(r.data.template);
    }).catch(() => toast.error("Nie udało się pobrać szablonu umowy."));
  }, []);

  const refreshPlaceholders = () => api.get("/admin/contract-template").then((r) => setData((d) => ({ ...d, placeholders: r.data.placeholders }))).catch(() => {});

  if (!data)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-600" />
      </div>
    );

  const insert = (key) => {
    const el = ref.current;
    const start = el?.selectionStart ?? template.length;
    const end = el?.selectionEnd ?? template.length;
    const next = template.slice(0, start) + key + template.slice(end);
    setTemplate(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + key.length, start + key.length);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/contract-template", { template });
      toast.success("Szablon umowy zapisany.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Nie udało się zapisać szablonu.");
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      await openPdf({ method: "post", url: "/admin/contract-template/preview", data: { template } });
    } catch {
      toast.error("Nie udało się wygenerować podglądu PDF.");
    } finally {
      setPreviewing(false);
    }
  };

  const dirty = template !== data.template;

  const syncOwu = async () => {
    if (dirty) {
      toast.error("Najpierw zapisz szablon umowy, a następnie zaktualizuj OWU.");
      return;
    }
    setSyncingOwu(true);
    try {
      const r = await api.post("/admin/owu/sync-from-contract");
      toast.success(`OWU zaktualizowane z treści umowy (${r.data.sections} sekcji).`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Nie udało się zaktualizować OWU.");
    } finally {
      setSyncingOwu(false);
    }
  };

  return (
    <div data-testid="contract-editor" className="space-y-6">
      <OrderFieldsEditor onSaved={refreshPlaceholders} />
      <ProformaSettings />
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6">
        <h2 className="font-display font-bold text-lg">Szablon umowy (PDF)</h2>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          Ta treść trafia do PDF umowy pobieranej przez klienta po złożeniu zamówienia oraz generowanej z zakładki Zamówienia.
          Pola w nawiasach klamrowych są automatycznie uzupełniane danymi z formularza — kliknij, aby wstawić w miejscu kursora.
          Znaczniki z zieloną obwódką to Twoje dodatkowe pola.
        </p>
        <div data-testid="contract-placeholders" className="mt-4 flex flex-wrap gap-2">
          {data.placeholders.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={`contract-placeholder-${p.key.replace(/[{}\s]/g, "")}`}
              title={p.label}
              onClick={() => insert(p.key)}
              className={`font-mono text-[11px] px-2.5 py-1 rounded-full transition-colors ${p.custom ? "bg-brand-teal/10 text-brand-teal border border-brand-teal/40 hover:bg-brand-teal/20" : "bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
            >
              {p.key}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
          {HINTS.map(([k, v]) => (
            <span key={k}><code className="font-mono text-foreground">{k}</code> — {v}</span>
          ))}
        </div>
      </div>

      <Textarea
        ref={ref}
        data-testid="contract-template-textarea"
        value={template}
        onChange={(e) => setTemplate(e.target.value)}
        spellCheck={false}
        className="font-mono text-xs leading-relaxed min-h-[60vh] bg-background"
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          data-testid="contract-reset-button"
          onClick={() => setTemplate(data.default)}
          className={`${btn} border border-border hover:bg-muted`}
        >
          <RotateCcw size={13} /> Przywróć domyślny
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span data-testid="contract-unsaved" className="text-xs text-amber-600 dark:text-amber-400">Niezapisane zmiany</span>}
          <button type="button" data-testid="owu-sync-button" onClick={syncOwu} disabled={syncingOwu} title="Przepisuje sekcje „Ogólne Warunki” z szablonu umowy do strony OWU" className={`${btn} border border-border hover:bg-muted`}>
            {syncingOwu ? <Loader2 size={13} className="animate-spin" /> : <FileCheck2 size={13} />} Zaktualizuj OWU z treści umowy
          </button>
          <button type="button" data-testid="contract-preview-button" onClick={preview} disabled={previewing} className={`${btn} border border-border hover:bg-muted`}>
            {previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Podgląd PDF
          </button>
          <button
            type="button"
            data-testid="contract-save-button"
            onClick={save}
            disabled={saving || !dirty}
            className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz szablon
          </button>
        </div>
      </div>
    </div>
  );
}
